// ============================================================================
// OrgChart — canvas zoomable/deplacable de l'organigramme (NAV v3, aligne OrgFocusView)
// ============================================================================

import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import {
  TransformComponent,
  TransformWrapper,
  type ReactZoomPanPinchRef,
} from 'react-zoom-pan-pinch';
import { FoldVertical, Maximize, Network, UnfoldVertical, ZoomIn, ZoomOut } from 'lucide-react';
import { EmptyState, GS_COMPONENTS } from '@gs/ui';
import { collectAncestorEmails, collectExpandableEmails, type OrgNode } from '../../lib/org/hierarchy';
import { useTranslation } from '../../i18n';
import { OrgTreeNode } from './OrgTreeNode';

const INITIAL_SCALE = 0.7;

const OVERLAY_BTN =
  'inline-flex items-center bg-white shadow-sm touch-manipulation dark:bg-slate-800';

export type OrgFocus = { email: string; key: number };

export const OrgChart = forwardRef<
  HTMLDivElement,
  {
    roots: OrgNode[];
    highlightedEmail?: string | null;
    focusedEmail?: string | null;
    focus?: OrgFocus | null;
    onDrillIn: (email: string) => void;
  }
>(function OrgChart({ roots, highlightedEmail, focusedEmail, focus, onDrillIn }, treeRef) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const expandable = useMemo(() => collectExpandableEmails(roots), [roots]);
  const transformRef = useRef<ReactZoomPanPinchRef | null>(null);

  useEffect(() => {
    if (!focus) return;
    const ancestors = collectAncestorEmails(roots, focus.email);
    setCollapsed((prev) => {
      if (ancestors.length === 0) return prev;
      const next = new Set(prev);
      for (const a of ancestors) next.delete(a);
      return next;
    });
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        transformRef.current?.zoomToElement(`org-node-${focus.email}`, 1, 400);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.key]);

  function toggle(email: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  }

  if (roots.length === 0) {
    return (
      <EmptyState
        icon={<Network size={48} aria-hidden="true" />}
        title={t('gest:organigramme.chart.emptyTitle')}
        description={t('gest:organigramme.chart.emptyDescription')}
      />
    );
  }

  return (
    <div
      data-org-canvas
      className={`${GS_COMPONENTS.card} relative h-[calc(100svh-280px)] min-h-96 overflow-hidden bg-slate-50 dark:bg-slate-900`} // ds-lint-exempt: org canvas = svh minus app chrome (~280px); no named layout token yet
    >
      <TransformWrapper
        ref={transformRef}
        initialScale={INITIAL_SCALE}
        minScale={0.25}
        maxScale={2}
        centerOnInit
        limitToBounds={false}
        wheel={{ step: 0.15 }}
        doubleClick={{ disabled: true }}
        panning={{ velocityDisabled: true }}
      >
        {({ zoomIn, zoomOut, resetTransform, centerView }) => (
          <>
            <div className="absolute right-3 top-3 z-10 flex gap-1.5">
              <button
                type="button"
                onClick={() => setCollapsed(new Set())}
                disabled={collapsed.size === 0}
                className={`${GS_COMPONENTS.btnSecondary} ${OVERLAY_BTN} gap-1.5 disabled:opacity-50`}
              >
                <UnfoldVertical size={16} aria-hidden="true" />
                {t('gest:organigramme.chart.expandAll')}
              </button>
              <button
                type="button"
                onClick={() => setCollapsed(new Set(expandable))}
                disabled={collapsed.size === expandable.length}
                className={`${GS_COMPONENTS.btnSecondary} ${OVERLAY_BTN} gap-1.5 disabled:opacity-50`}
              >
                <FoldVertical size={16} aria-hidden="true" />
                {t('gest:organigramme.chart.collapseAll')}
              </button>
            </div>

            <div className="absolute bottom-3 right-3 z-10 flex flex-col gap-1">
              <button
                type="button"
                aria-label={t('gest:organigramme.chart.zoomIn')}
                onClick={() => zoomIn()}
                className={`${GS_COMPONENTS.btnSecondary} ${OVERLAY_BTN} justify-center`}
              >
                <ZoomIn size={16} aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label={t('gest:organigramme.chart.zoomOut')}
                onClick={() => zoomOut()}
                className={`${GS_COMPONENTS.btnSecondary} ${OVERLAY_BTN} justify-center`}
              >
                <ZoomOut size={16} aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label={t('gest:organigramme.chart.resetView')}
                onClick={() => {
                  resetTransform(0);
                  centerView(INITIAL_SCALE, 200);
                }}
                className={`${GS_COMPONENTS.btnSecondary} ${OVERLAY_BTN} justify-center`}
              >
                <Maximize size={16} aria-hidden="true" />
              </button>
            </div>

            <TransformComponent wrapperClass="!h-full !w-full" contentClass="p-12">
              <div ref={treeRef} className="org-tree w-max">
                <ul>
                  {roots.map((root) => (
                    <OrgTreeNode
                      key={root.email}
                      node={root}
                      depth={0}
                      collapsed={collapsed}
                      highlightedEmail={highlightedEmail}
                      focusedEmail={focusedEmail}
                      onToggle={toggle}
                      onDrillIn={onDrillIn}
                    />
                  ))}
                </ul>
              </div>
            </TransformComponent>
          </>
        )}
      </TransformWrapper>
    </div>
  );
});
